import type { Race } from "./types";

/**
 * Every 2026 Senate (35, incl. the Florida and Ohio specials) and governor
 * (36) race on the Nov 3 ballot, with general-election nominees verified
 * against Wikipedia race pages plus news/Ballotpedia sources as of
 * Oct 27, 2026. Cook ratings: Senate Oct 20, governors Oct 23 update.
 *
 * `left` is the Democrat, or the Democratic-aligned independent where no
 * Democrat is on the ballot (NE, ID, SD) or where the independent is the
 * main challenger (MT). `incumbentParty` is the party holding the seat
 * today (appointees included), which decides what counts as a flip.
 */
export const RACES: Race[] = [
  {
    cook: "Toss-up",
    incumbent: "Dan Sullivan",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Mary Peltola", party: "D" },
    notes: "Top-four ballot with ranked-choice voting",
    office: "senate",
    others: [
      { name: "Gerald Heikes", party: "R" },
      {
        affiliation: "no party listed",
        name: "Daniel J. Sullivan Jr.",
        party: "I",
        short: "Sullivan Jr.",
      },
    ],
    right: { name: "Dan Sullivan", party: "R" },
    special: false,
    state: "AK",
  },
  {
    cook: "Solid R",
    incumbent: "Tommy Tuberville",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Everett Wess", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Barry Moore", party: "R" },
    special: false,
    state: "AL",
  },
  {
    cook: "Solid R",
    incumbent: "Tom Cotton",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Hallie Shoffner", party: "D" },
    notes: null,
    office: "senate",
    others: [{ affiliation: "Libertarian", name: "Jeff Wadlin", party: "I" }],
    right: { name: "Tom Cotton", party: "R" },
    special: false,
    state: "AR",
  },
  {
    cook: "Solid D",
    incumbent: "John Hickenlooper",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "John Hickenlooper", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Mark Baisley", party: "R" },
    special: false,
    state: "CO",
  },
  {
    cook: "Solid D",
    incumbent: "Chris Coons",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Chris Coons", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Michael Katz", party: "R" },
    special: false,
    state: "DE",
  },
  {
    cook: "Solid R",
    incumbent: "Ashley Moody",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Angie Nixon", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Ashley Moody", party: "R" },
    special: true,
    state: "FL",
  },
  {
    cook: "Likely D",
    incumbent: "Jon Ossoff",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Jon Ossoff", party: "D" },
    notes: "Majority required, or a Dec 1 runoff",
    office: "senate",
    others: [],
    right: { name: "Mike Collins", party: "R" },
    special: false,
    state: "GA",
  },
  {
    cook: "Toss-up",
    incumbent: "Joni Ernst",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Josh Turek", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Ashley Hinson", party: "R" },
    special: false,
    state: "IA",
  },
  {
    cook: "Solid R",
    incumbent: "Jim Risch",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Todd Achilles", party: "I" },
    notes: "No Democrat on the ballot; independent Achilles is Dem-aligned",
    office: "senate",
    others: [],
    right: { name: "Jim Risch", party: "R" },
    special: false,
    state: "ID",
  },
  {
    cook: "Solid D",
    incumbent: "Dick Durbin",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Juliana Stratton", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Don Tracy", party: "R" },
    special: false,
    state: "IL",
  },
  {
    cook: "Toss-up",
    incumbent: "Roger Marshall",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Adam Hamilton", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Roger Marshall", party: "R" },
    special: false,
    state: "KS",
  },
  {
    cook: "Solid R",
    incumbent: "Mitch McConnell",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Charles Booker", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Andy Barr", party: "R" },
    special: false,
    state: "KY",
  },
  {
    cook: "Solid R",
    incumbent: "Bill Cassidy",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Jamie Davis", party: "D" },
    notes: "Cassidy lost the new closed primary; plurality general",
    office: "senate",
    others: [],
    right: { name: "Julia Letlow", party: "R" },
    special: false,
    state: "LA",
  },
  {
    cook: "Solid D",
    incumbent: "Ed Markey",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Ed Markey", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "John Deaton", party: "R" },
    special: false,
    state: "MA",
  },
  {
    cook: "Toss-up",
    incumbent: "Susan Collins",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Troy Jackson", party: "D" },
    notes: "Ranked-choice general",
    office: "senate",
    others: [],
    right: { name: "Susan Collins", party: "R" },
    special: false,
    state: "ME",
  },
  {
    cook: "Toss-up",
    incumbent: "Gary Peters",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Abdul El-Sayed", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Mike Rogers", party: "R" },
    special: false,
    state: "MI",
  },
  {
    cook: "Lean D",
    incumbent: "Tina Smith",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Peggy Flanagan", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Michele Tafoya", party: "R" },
    special: false,
    state: "MN",
  },
  {
    cook: "Likely R",
    incumbent: "Cindy Hyde-Smith",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Scott Colom", party: "D" },
    notes: null,
    office: "senate",
    others: [{ name: "Ty Pinkins", party: "I" }],
    right: { name: "Cindy Hyde-Smith", party: "R" },
    special: false,
    state: "MS",
  },
  {
    cook: "Solid R",
    incumbent: "Steve Daines",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Seth Bodnar", party: "I" },
    notes:
      "Daines retired; Tester-backed independent Bodnar plus Democrat Bankhead",
    office: "senate",
    others: [
      { name: "Alani Bankhead", party: "D" },
      { affiliation: "Libertarian", name: "Kyle Austin", party: "I" },
    ],
    right: { name: "Kurt Alme", party: "R" },
    special: false,
    state: "MT",
  },
  {
    cook: "Likely D",
    incumbent: "Thom Tillis",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Roy Cooper", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Michael Whatley", party: "R" },
    special: false,
    state: "NC",
  },
  {
    cook: "Likely R",
    incumbent: "Pete Ricketts",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { discounted: true, name: "Dan Osborn", party: "I" },
    notes: "No Democrat on the ballot; Osborn won't caucus with either party",
    office: "senate",
    others: [
      { affiliation: "America First", name: "Chuck Conboy", party: "I" },
      {
        affiliation: "Nebraska Working People Party",
        name: "Robin Richards",
        party: "I",
      },
    ],
    right: { name: "Pete Ricketts", party: "R" },
    special: false,
    state: "NE",
  },
  {
    cook: "Lean D",
    incumbent: "Jeanne Shaheen",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Chris Pappas", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "John E. Sununu", party: "R" },
    special: false,
    state: "NH",
  },
  {
    cook: "Solid D",
    incumbent: "Cory Booker",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Cory Booker", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Justin Murphy", party: "R" },
    special: false,
    state: "NJ",
  },
  {
    cook: "Solid D",
    incumbent: "Ben Ray Luján",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Ben Ray Luján", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Larry Marker", party: "R" },
    special: false,
    state: "NM",
  },
  {
    cook: "Toss-up",
    incumbent: "Jon Husted",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Sherrod Brown", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Jon Husted", party: "R" },
    special: true,
    state: "OH",
  },
  {
    cook: "Solid R",
    incumbent: "Alan Armstrong",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "N'Kiyla Jasmine Thomas", party: "D" },
    notes: "Appointee Armstrong (Mullin went to DHS) isn't running",
    office: "senate",
    others: [],
    right: { name: "Kevin Hern", party: "R" },
    special: false,
    state: "OK",
  },
  {
    cook: "Solid D",
    incumbent: "Jeff Merkley",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Jeff Merkley", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "David Brock Smith", party: "R" },
    special: false,
    state: "OR",
  },
  {
    cook: "Solid D",
    incumbent: "Jack Reed",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Jack Reed", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Raymond McKay", party: "R" },
    special: false,
    state: "RI",
  },
  {
    cook: "Lean R",
    incumbent: "Darline Graham",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Annie Andrews", party: "D" },
    notes: "Appointed after Lindsey Graham's death in July",
    office: "senate",
    others: [],
    right: { name: "Darline Graham", party: "R" },
    special: false,
    state: "SC",
  },
  {
    cook: "Solid R",
    incumbent: "Mike Rounds",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Brian Bengs", party: "I" },
    notes: "No Democrat on the ballot; independent Bengs",
    office: "senate",
    others: [],
    right: { name: "Mike Rounds", party: "R" },
    special: false,
    state: "SD",
  },
  {
    cook: "Solid R",
    incumbent: "Bill Hagerty",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Marquita Bradshaw", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Bill Hagerty", party: "R" },
    special: false,
    state: "TN",
  },
  {
    cook: "Toss-up",
    incumbent: "John Cornyn",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "James Talarico", party: "D" },
    notes: null,
    office: "senate",
    others: [{ affiliation: "Libertarian", name: "Ted Brown", party: "I" }],
    right: { name: "Ken Paxton", party: "R" },
    special: false,
    state: "TX",
  },
  {
    cook: "Solid D",
    incumbent: "Mark Warner",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Mark Warner", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Bert Mizusawa", party: "R" },
    special: false,
    state: "VA",
  },
  {
    cook: "Solid R",
    incumbent: "Shelley Moore Capito",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Rachel Fetty Anderson", party: "D" },
    notes: null,
    office: "senate",
    others: [
      {
        affiliation: "Constitution Party",
        name: "S. Marshall Wilson",
        party: "I",
      },
    ],
    right: { name: "Shelley Moore Capito", party: "R" },
    special: false,
    state: "WV",
  },
  {
    cook: "Solid R",
    incumbent: "Cynthia Lummis",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "James W. Byrd", party: "D" },
    notes: null,
    office: "senate",
    others: [],
    right: { name: "Harriet Hageman", party: "R" },
    special: false,
    state: "WY",
  },
  {
    cook: "Lean D",
    incumbent: "Mike Dunleavy",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Jonathan Kreiss-Tomkins", party: "D" },
    notes: "Top-four ranked-choice: one Democrat vs three Republicans",
    office: "governor",
    others: [
      { name: "Dave Bronson", party: "R" },
      { name: "Treg Taylor", party: "R" },
    ],
    right: { name: "Bernadette Wilson", party: "R" },
    special: false,
    state: "AK",
  },
  {
    cook: "Solid R",
    incumbent: "Kay Ivey",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Doug Jones", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Tommy Tuberville", party: "R" },
    special: false,
    state: "AL",
  },
  {
    cook: "Solid R",
    incumbent: "Sarah Huckabee Sanders",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Fredrick Love", party: "D" },
    notes: null,
    office: "governor",
    others: [{ affiliation: "Libertarian", name: "Colt Shelby", party: "I" }],
    right: {
      name: "Sarah Huckabee Sanders",
      party: "R",
      short: "Huckabee Sanders",
    },
    special: false,
    state: "AR",
  },
  {
    cook: "Lean D",
    incumbent: "Katie Hobbs",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Katie Hobbs", party: "D" },
    notes: null,
    office: "governor",
    others: [
      { affiliation: "No Labels", name: "Teri Hourihan", party: "I" },
      { affiliation: "Green Party", name: "Risa Lombardo", party: "I" },
    ],
    right: { name: "Andy Biggs", party: "R" },
    special: false,
    state: "AZ",
  },
  {
    cook: "Solid D",
    incumbent: "Gavin Newsom",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Xavier Becerra", party: "D" },
    notes: "Top-two general",
    office: "governor",
    others: [],
    right: { name: "Steve Hilton", party: "R" },
    special: false,
    state: "CA",
  },
  {
    cook: "Solid D",
    incumbent: "Jared Polis",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Phil Weiser", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Victor Marx", party: "R" },
    special: false,
    state: "CO",
  },
  {
    cook: "Solid D",
    incumbent: "Ned Lamont",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Ned Lamont", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Ryan Fazio", party: "R" },
    special: false,
    state: "CT",
  },
  {
    cook: "Lean R",
    incumbent: "Ron DeSantis",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "David Jolly", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Byron Donalds", party: "R" },
    special: false,
    state: "FL",
  },
  {
    cook: "Toss-up",
    incumbent: "Brian Kemp",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Keisha Lance Bottoms", party: "D", short: "Lance Bottoms" },
    notes: "Majority required, or a Dec 1 runoff",
    office: "governor",
    others: [],
    right: { name: "Rick Jackson", party: "R" },
    special: false,
    state: "GA",
  },
  {
    cook: "Solid D",
    incumbent: "Josh Green",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Josh Green", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Gary Cordery", party: "R" },
    special: false,
    state: "HI",
  },
  {
    cook: "Lean D",
    incumbent: "Kim Reynolds",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Rob Sand", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Zach Lahn", party: "R" },
    special: false,
    state: "IA",
  },
  {
    cook: "Solid R",
    incumbent: "Brad Little",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Terri Pickens", party: "D" },
    notes: null,
    office: "governor",
    others: [{ name: "John Stegner", party: "I" }],
    right: { name: "Brad Little", party: "R" },
    special: false,
    state: "ID",
  },
  {
    cook: "Solid D",
    incumbent: "JB Pritzker",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "JB Pritzker", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Darren Bailey", party: "R" },
    special: false,
    state: "IL",
  },
  {
    cook: "Lean R",
    incumbent: "Laura Kelly",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Cindy Holscher", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Ty Masterson", party: "R" },
    special: false,
    state: "KS",
  },
  {
    cook: "Solid D",
    incumbent: "Maura Healey",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Maura Healey", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Mike Minogue", party: "R" },
    special: false,
    state: "MA",
  },
  {
    cook: "Solid D",
    incumbent: "Wes Moore",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Wes Moore", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Dan Cox", party: "R" },
    special: false,
    state: "MD",
  },
  {
    cook: "Solid D",
    incumbent: "Janet Mills",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Hannah Pingree", party: "D" },
    notes: null,
    office: "governor",
    others: [{ name: "Rick Bennett", party: "I" }],
    right: { name: "Bobby Charles", party: "R" },
    special: false,
    state: "ME",
  },
  {
    cook: "Lean D",
    incumbent: "Gretchen Whitmer",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Jocelyn Benson", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "John James", party: "R" },
    special: false,
    state: "MI",
  },
  {
    cook: "Solid D",
    incumbent: "Tim Walz",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Amy Klobuchar", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Lisa Demuth", party: "R" },
    special: false,
    state: "MN",
  },
  {
    cook: "Likely R",
    incumbent: "Jim Pillen",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Lynne Walz", party: "D" },
    notes: null,
    office: "governor",
    others: [
      { affiliation: "America First", name: "Brett Lindstrom", party: "I" },
      { affiliation: "Legal Marijuana NOW", name: "Rick Beard", party: "I" },
    ],
    right: { name: "Jim Pillen", party: "R" },
    special: false,
    state: "NE",
  },
  {
    cook: "Likely R",
    incumbent: "Kelly Ayotte",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Cinde Warmington", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Kelly Ayotte", party: "R" },
    special: false,
    state: "NH",
  },
  {
    cook: "Solid D",
    incumbent: "Michelle Lujan Grisham",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Deb Haaland", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Gregg Hull", party: "R" },
    special: false,
    state: "NM",
  },
  {
    cook: "Toss-up",
    incumbent: "Joe Lombardo",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Aaron Ford", party: "D" },
    notes: null,
    office: "governor",
    others: [
      {
        affiliation: "No Political Party",
        name: "Danielle Ford",
        party: "I",
      },
    ],
    right: { name: "Joe Lombardo", party: "R" },
    special: false,
    state: "NV",
  },
  {
    cook: "Solid D",
    incumbent: "Kathy Hochul",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Kathy Hochul", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Bruce Blakeman", party: "R" },
    special: false,
    state: "NY",
  },
  {
    cook: "Lean D",
    incumbent: "Mike DeWine",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Amy Acton", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Vivek Ramaswamy", party: "R" },
    special: false,
    state: "OH",
  },
  {
    cook: "Likely R",
    incumbent: "Kevin Stitt",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Cyndi Munson", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Mike Mazzei", party: "R" },
    special: false,
    state: "OK",
  },
  {
    cook: "Lean D",
    incumbent: "Tina Kotek",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Tina Kotek", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Christine Drazan", party: "R" },
    special: false,
    state: "OR",
  },
  {
    cook: "Solid D",
    incumbent: "Josh Shapiro",
    incumbentParty: "D",
    incumbentRunning: true,
    left: { name: "Josh Shapiro", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Stacy Garrity", party: "R" },
    special: false,
    state: "PA",
  },
  {
    cook: "Solid D",
    incumbent: "Dan McKee",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "Helena Foulkes", party: "D" },
    notes: "Main challenger is independent Ken Block, not the Republican",
    office: "governor",
    others: [{ name: "Ken Block", party: "I" }],
    right: { name: "Aaron Guckian", party: "R" },
    special: false,
    state: "RI",
  },
  {
    cook: "Solid R",
    incumbent: "Henry McMaster",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Jermaine Johnson", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Alan Wilson", party: "R" },
    special: false,
    state: "SC",
  },
  {
    cook: "Solid R",
    incumbent: "Larry Rhoden",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Dan Ahlers", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Larry Rhoden", party: "R" },
    special: false,
    state: "SD",
  },
  {
    cook: "Solid R",
    incumbent: "Bill Lee",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Jerri Green", party: "D" },
    notes: null,
    office: "governor",
    others: [
      { name: "David Hatley", party: "I" },
      { name: "Lauren Pinkston", party: "I" },
    ],
    right: { name: "Marsha Blackburn", party: "R" },
    special: false,
    state: "TN",
  },
  {
    cook: "Lean R",
    incumbent: "Greg Abbott",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Gina Hinojosa", party: "D" },
    notes: null,
    office: "governor",
    others: [{ affiliation: "Libertarian", name: "Pat Dixon", party: "I" }],
    right: { name: "Greg Abbott", party: "R" },
    special: false,
    state: "TX",
  },
  {
    cook: "Likely R",
    incumbent: "Phil Scott",
    incumbentParty: "R",
    incumbentRunning: true,
    left: { name: "Amanda Janoo", party: "D" },
    notes: "Majority required, or the legislature decides",
    office: "governor",
    others: [],
    right: { name: "Phil Scott", party: "R" },
    special: false,
    state: "VT",
  },
  {
    cook: "Toss-up",
    incumbent: "Tony Evers",
    incumbentParty: "D",
    incumbentRunning: false,
    left: { name: "David Crowley", party: "D" },
    notes: null,
    office: "governor",
    others: [],
    right: { name: "Tom Tiffany", party: "R" },
    special: false,
    state: "WI",
  },
  {
    cook: "Solid R",
    incumbent: "Mark Gordon",
    incumbentParty: "R",
    incumbentRunning: false,
    left: { name: "Kenneth Casner", party: "D" },
    notes: null,
    office: "governor",
    others: [
      { affiliation: "Constitution Party", name: "Rebecca Bextel", party: "I" },
    ],
    right: { name: "Eric Barlow", party: "R" },
    special: false,
    state: "WY",
  },
];
